import classnames from 'classnames/bind';
import type { ComponentProps, ReactNode } from 'react';
import { Theme } from 'app/lib/constants';
import { Icon, IconSize, type IconVariant } from 'app/components/core/Icon';
import { useTheme } from 'app/components/core/ThemeProvider';
import { BannerVariant } from './constants';
import * as styles from './Banner.css';

const cx = classnames.bind(styles);

const icons: Record<string, IconVariant> = {
    [BannerVariant.SUCCESS]: 'checkmark',
    [BannerVariant.WARNING]: 'status-warning-solid',
    [BannerVariant.ERROR]: 'status-alert-solid',
    [BannerVariant.INFO]: 'info-solid'
} as const;

const titles: Record<string, string> = {
    [BannerVariant.SUCCESS]: 'Success',
    [BannerVariant.WARNING]: 'Warning',
    [BannerVariant.ERROR]: 'Error',
    [BannerVariant.INFO]: 'Info',
    [BannerVariant.SIMPLE]: 'Simple'
} as const;

export interface Props extends ComponentProps<'div'> {
    testId?: string;
    className?: string;
    variant?: BannerVariant;
    children?: ReactNode;
    noIcon?: boolean;
    theme?: Theme;
    tooltip?: string;
}

export function Banner({
    testId,
    className,
    variant = BannerVariant.INFO,
    children,
    noIcon = false,
    theme: themeDefault = Theme.LIGHT,
    tooltip,
    ...props
}: Props) {
    const { themeClassname } = useTheme(themeDefault);
    const classes = cx('base', variant, className, themeClassname);
    const icon = icons[variant];
    const title = tooltip ?? (typeof children === 'string' ? children : undefined);

    return (
        <div role="alert" data-testid={testId} className={classes} {...props}>
            {icon && !noIcon && (
                <Icon
                    className={styles.icon}
                    size={IconSize.EXTRA_SMALL}
                    variant={icon}
                    aria-hidden="false"
                    title={titles[variant]}
                />
            )}
            <div className={styles.content} title={title}>
                {children}
            </div>
        </div>
    );
}
